/**
 * Reine Hilfen der Stationen 1–3 des Leitfadens (Abschnitt A „Vorhaben",
 * docs/planungsphase-leitfaden-plan.md). Ohne React, damit sie ohne jsdom
 * testbar sind (Plan, Falle 2) und die Stationskomponenten duenn bleiben.
 */

import { offeneMerkmale, restOffenVermerkt } from '../leitfaden';
import type { Planungsphase, ProjektMerkmale } from '../types';

// ═══════════════════════════════════════════════════════════════════════════
// STATION 3: „REST BLEIBT OFFEN" (Entscheid E-L6)
// ═══════════════════════════════════════════════════════════════════════════

export interface RestOffenEingang {
    /** Der GESPEICHERTE Kopfsatz (`a.phase`) — traegt auch den Vermerk
     *  (`merkmale.leitfadenRestOffen`). */
    phase: Pick<Planungsphase, 'merkmale'> | null;
    /** Die Merkmale des Entwurfs (`a.grundlagen.merkmale`). */
    entwurfMerkmale: ProjektMerkmale | null | undefined;
    schmutzig: boolean;
    /** `a.fragen.length`. */
    fragenAnzahl: number;
    isReadOnly: boolean;
    abgeschlossen: boolean;
}

/**
 *   knopf          — [Rest bleibt offen] fuer den gespeicherten Stand
 *   vermerkt       — schon bestaetigt (Vermerk am Kopfsatz, passender Stand)
 *   erst_speichern — Entwurf ungespeichert, darin Merkmale offen
 *   null           — nichts zeigen
 */
export type RestOffenZeile =
    | { art: 'knopf'; offen: number }
    | { art: 'vermerkt' }
    | { art: 'erst_speichern'; offen: number }
    | null;

/**
 * Welche Zeile unter den Merkmalen steht. „Rest bleibt offen" bezieht sich
 * auf den GESPEICHERTEN Stand (der Vermerk am Kopfsatz haengt am
 * Fingerabdruck der gespeicherten Merkmale, `restOffenVermerkt`); deshalb
 * kein Knopf, solange der Entwurf ungespeichert ist.
 *
 * Dieselbe Regel entscheidet, ob `bestaetigeRestOffen` im Modul schreiben
 * darf: nur bei `knopf` — dann ist nichts ungespeichert, die Phase steht,
 * sie ist offen und beschreibbar.
 *
 * Steht der Katalog schon, ist die Station ohnehin erledigt — dann nichts.
 * Den Vermerk sieht auch, wer nur ansehen darf; den Knopf nicht.
 */
export const restOffenZeile = (e: RestOffenEingang): RestOffenZeile => {
    if (e.fragenAnzahl > 0) return null;
    // Ohne gespeicherte Phase gibt es nichts zu vermerken. Vorsicht:
    // `offeneMerkmale(null)` liefert ALLE Schluessel.
    const gespeichert = e.phase ? offeneMerkmale(e.phase.merkmale).length : 0;
    if (e.phase && restOffenVermerkt(e.phase.merkmale) && gespeichert > 0) return { art: 'vermerkt' };
    if (e.isReadOnly || e.abgeschlossen) return null;
    if (e.schmutzig) {
        const entwurf = offeneMerkmale(e.entwurfMerkmale).length;
        return entwurf > 0 ? { art: 'erst_speichern', offen: entwurf } : null;
    }
    return gespeichert > 0 ? { art: 'knopf', offen: gespeichert } : null;
};
